import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronRight, Monitor } from 'lucide-react';
import { resolveAssetUrl } from '@/lib/assetUrl';
import { sanitizeHtml } from '@/lib/sanitizeHtml';

/**
 * "Whistle" landing template — CogniEtica-branded institutional layout.
 *
 * Big serif slug at top-left (uppercase), "COGNIETICA" tagline underneath,
 * hero image band, two-column body: institutional text on the left, sticky
 * action card (Submit / Track) + support block on the right, TenantFooter
 * below (rendered by the caller).
 *
 * Used by tenants that opted into `layout_style: 'whistle'`. Shared between
 * the public landing (WhistleLanding) and the live preview in Settings.
 */
const WhistleLandingBlock = ({
  tenantSlug,
  tenantName,
  heroImageUrl,
  primaryColor,
  institutionalText,
  supportEmail,
  supportSignature,
  onStart,
  interactive = true,
  sticky = true,
}) => {
  const color = primaryColor || '#0F2A5C';
  const resolvedHero = resolveAssetUrl(heroImageUrl);
  const slugDisplay = (tenantSlug || tenantName || 'CANAL').toUpperCase();

  // Scale the brand slug down as it gets longer so mobile viewports don't
  // overflow the header. Short slugs (≤6 chars) keep the premium 3xl/4xl
  // treatment; medium (7-12) and long (13+) shrink progressively.
  const slugLength = slugDisplay.length;
  const slugSizeClass = slugLength <= 6
    ? 'text-3xl sm:text-4xl tracking-[0.08em]'
    : slugLength <= 12
      ? 'text-2xl sm:text-3xl tracking-[0.06em]'
      : 'text-lg sm:text-2xl tracking-[0.04em]';

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="whistle-landing-block">
      {/* Header — brand slug top-left, language selector top-right */}
      <header className={`w-full bg-white border-b border-slate-100 ${sticky ? 'sticky top-0 z-40' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex items-start justify-between gap-4">
          <div className="flex flex-col min-w-0 flex-1">
            <span
              className={`font-serif ${slugSizeClass} leading-none truncate max-w-full`}
              style={{ color }}
              data-testid="whistle-slug-display"
              title={slugDisplay}
            >
              {slugDisplay}
            </span>
            <span
              className="mt-1 text-[10px] font-semibold tracking-[0.35em] text-slate-500"
              data-testid="whistle-tagline"
            >
              COGNIETICA
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2 border border-slate-200 rounded-md px-3 py-1.5 text-sm text-slate-600 shrink-0" data-testid="whistle-lang-selector">
            Português (Portuguese)
            <ChevronRight className="w-4 h-4 rotate-90" />
          </div>
        </div>
      </header>

      {/* Hero band — full-width image */}
      <div
        className="w-full h-[220px] sm:h-[320px] bg-slate-800"
        style={{
          backgroundColor: color,
          backgroundImage: resolvedHero ? `url(${resolvedHero})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="whistle-hero-band"
      />

      {/* Body — two columns */}
      <main className="flex-1 max-w-7xl mx-auto px-6 sm:px-10 py-14 grid grid-cols-1 lg:grid-cols-3 gap-12 w-full">
        {/* Left: welcome heading + institutional text */}
        <div className="lg:col-span-2 space-y-5">
          <h1
            className="text-2xl sm:text-3xl font-semibold leading-snug"
            style={{ color }}
            data-testid="whistle-welcome-title"
          >
            Bem-vindo ao canal de denúncia interna {slugDisplay}
          </h1>
          {institutionalText ? (
            <div
              className="prose prose-slate max-w-none prose-headings:mt-8 prose-headings:mb-3 prose-headings:font-semibold prose-p:leading-relaxed"
              style={{ '--tw-prose-headings': color }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(institutionalText) }}
              data-testid="whistle-institutional-text"
            />
          ) : (
            <p className="text-slate-700 leading-relaxed">
              Encorajamo-lo(a) a denunciar qualquer infração ou suspeita de infração
              neste canal de denúncia interna. Asseguramos sempre o anonimato e a
              confidencialidade da sua identidade, desde a submissão da denúncia
              até à conclusão do processo.
            </p>
          )}
        </div>

        {/* Right: sticky action card */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 space-y-4" data-testid="whistle-actions-card">
            {interactive ? (
              <button
                type="button"
                onClick={onStart}
                className="w-full px-6 py-4 rounded-md font-semibold text-white text-sm tracking-wide uppercase shadow-md hover:opacity-90 transition-all duration-200 inline-flex items-center justify-between"
                style={{ backgroundColor: color }}
                data-testid="whistle-start-report-cta"
              >
                Submeta a denúncia aqui
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <span
                className="w-full px-6 py-4 rounded-md font-semibold text-white text-sm tracking-wide uppercase shadow-md inline-flex items-center justify-between cursor-default"
                style={{ backgroundColor: color }}
                data-testid="whistle-start-report-cta-preview"
              >
                Submeta a denúncia aqui
                <ChevronRight className="w-4 h-4" />
              </span>
            )}

            {interactive ? (
              <Link
                to="/seguimento"
                className="w-full px-6 py-4 rounded-md font-semibold text-sm tracking-wide uppercase border-2 bg-white hover:bg-slate-50 transition-colors duration-200 inline-flex items-center justify-center"
                style={{ borderColor: color, color }}
                data-testid="whistle-follow-up-link"
              >
                Acompanhe o seu caso
              </Link>
            ) : (
              <span
                className="w-full px-6 py-4 rounded-md font-semibold text-sm tracking-wide uppercase border-2 bg-white inline-flex items-center justify-center cursor-default"
                style={{ borderColor: color, color }}
                data-testid="whistle-follow-up-link-preview"
              >
                Acompanhe o seu caso
              </span>
            )}

            <a
              href="#privacy"
              className="block text-sm font-medium hover:underline pt-2"
              style={{ color }}
              data-testid="whistle-privacy-link"
            >
              Política de privacidade e proteção de dados pessoais ›
            </a>

            {(supportEmail || supportSignature) && (
              <div className="pt-6 border-t border-slate-100">
                <h4
                  className="text-sm font-semibold mb-3"
                  style={{ color }}
                  data-testid="whistle-support-title"
                >
                  Suporte
                </h4>
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <Monitor className="w-8 h-8 text-slate-400 shrink-0" />
                  {supportSignature ? (
                    <div
                      className="prose prose-slate prose-sm max-w-none prose-p:leading-relaxed prose-p:my-0 prose-a:break-all"
                      style={{ '--tw-prose-links': color }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(supportSignature) }}
                      data-testid="whistle-support-signature"
                    />
                  ) : (
                    <p className="leading-relaxed">
                      Se tiver alguma dúvida sobre o funcionamento do canal, contacte o
                      Departamento de Compliance da {tenantName || 'organização'}, através do seguinte
                      endereço de correio eletrónico:{' '}
                      <a
                        href={`mailto:${supportEmail}`}
                        className="font-medium hover:underline break-all"
                        style={{ color }}
                        data-testid="whistle-support-email"
                      >
                        {supportEmail}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            )}

            {!supportEmail && !supportSignature && (
              <div className="pt-6 border-t border-slate-100 flex items-start gap-3 text-sm text-slate-500">
                <Mail className="w-5 h-5 text-slate-400 shrink-0" />
                <p>Configure um email de suporte ou uma mensagem personalizada nas Definições do canal.</p>
              </div>
            )}
          </div>
        </aside>
      </main>
    </div>
  );
};

export default WhistleLandingBlock;
